import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { getTelegram, haptic } from "../../lib/telegram.ts";
import { Loader } from "../../components/Loader.tsx";
import {
  IconCalendar,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconHeartFilled,
  IconFamily,
  IconHomeHeart,
  IconLayers,
  IconYouth,
  IconKids,
  IconBriefcase,
  IconShield,
  IconFoldedHands,
  IconDove,
  IconAcademicCap,
} from "../../components/Icons.tsx";
import { EventCard } from "../../components/EventCard.tsx";
import { useEvents, useScheduleCategories } from "@/features/events/hooks";
import type { Event } from "../../lib/graphql.ts";
import { ErrorDisplay } from "../../components/ErrorDisplay.tsx";
import { safeJsonParse } from "../../lib/json-utils.ts";
import {
  getDateKeyBangkok,
  getTodayDateKeyBangkok,
  getWeekStartDateKeyBangkok,
  addDaysToDateKey,
  formatDateShortBangkok,
  getDayOfMonthAndWeekdayBangkok,
  type BangkokDateKey,
} from "../../lib/bangkok-time.ts";
import { useOnRestoreFromBackground } from "../../lib/use-lifecycle.ts";
import { APP_TIMEZONE } from "../../config.ts";

// ============================================================================
// Constants
// ============================================================================

const WEEKDAYS_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const WEEKDAYS_FULL = [
  "Понедельник",
  "Вторник",
  "Среда",
  "Четверг",
  "Пятница",
  "Суббота",
  "Воскресенье",
];
const MONTHS_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const SCHEDULE_STATE_KEY = "schedule_page_state";

const CARD_CLASS =
  "rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50";

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  home_groups: IconHomeHeart,
  youth: IconYouth,
  kids: IconKids,
  family: IconFamily,
  business: IconBriefcase,
  ministers: IconShield,
  prayer: IconFoldedHands,
  worship: IconDove,
  training: IconAcademicCap,
};

const CATEGORY_COLORS: Record<string, string> = {
  home_groups: "bg-rose-50/80 text-rose-600/75",
  youth: "bg-violet-50/80 text-violet-700/75",
  kids: "bg-amber-50/80 text-amber-700/75",
  family: "bg-pink-50/80 text-pink-600/75",
  business: "bg-blue-50/80 text-blue-700/75",
  ministers: "bg-emerald-50/80 text-emerald-700/75",
  prayer: "bg-indigo-50/80 text-indigo-700/75",
  worship: "bg-red-50/80 text-red-600/75",
  training: "bg-teal-50/80 text-teal-700/75",
};

interface SchedulePageState {
  weekStartDateKey: string;
  selectedDateKey: string;
  selectedCategoryIds: string[];
  showWholeWeek: boolean;
}

function isValidDateKey(k: unknown): k is string {
  return typeof k === "string" && /^\d{4}-\d{2}-\d{2}$/.test(k);
}

function readScheduleStateFromStorage(): SchedulePageState | null {
  if (typeof sessionStorage === "undefined") return null;
  
  const raw = sessionStorage.getItem(SCHEDULE_STATE_KEY);
  if (!raw) return null;
  
  const parsed = safeJsonParse<Record<string, unknown> | null>(raw, null);
  if (!parsed || typeof parsed !== "object") return null;
  
  const weekStart = parsed.weekStartDateKey;
  const selected = parsed.selectedDateKey;
  if (!isValidDateKey(weekStart) || !isValidDateKey(selected)) return null;
  
  const categoryIds = Array.isArray(parsed.selectedCategoryIds)
    ? (parsed.selectedCategoryIds.filter((id: unknown) => typeof id === "string") as string[])
    : [];
  
  return {
    weekStartDateKey: weekStart,
    selectedDateKey: selected,
    selectedCategoryIds: categoryIds,
    showWholeWeek: Boolean(parsed.showWholeWeek),
  };
}

function getInitialScheduleState(): SchedulePageState {
  const saved = readScheduleStateFromStorage();
  if (saved) return saved;
  return {
    weekStartDateKey: getWeekStartDateKeyBangkok(),
    selectedDateKey: getTodayDateKeyBangkok(),
    selectedCategoryIds: [],
    showWholeWeek: false,
  };
}

// ============================================================================
// Components
// ============================================================================

interface WeekDaySelectorProps {
  weekDayDateKeys: BangkokDateKey[];
  selectedDateKey: BangkokDateKey;
  onSelectDateKey: (dateKey: BangkokDateKey) => void;
  eventsByDay: Map<string, Event[]>;
  todayDateKey: BangkokDateKey;
  /** When false, no day is shown as selected (e.g. when "whole week" view is on). */
  selectionVisible?: boolean;
  /** Date keys (YYYY-MM-DD) where user has at least one RSVP. */
  dateKeysWithMyRsvp?: Set<string>;
}

/** Horizontal week day selector (Bangkok dates). */
function WeekDaySelector({
  weekDayDateKeys,
  selectedDateKey,
  onSelectDateKey,
  eventsByDay,
  todayDateKey,
  selectionVisible = true,
  dateKeysWithMyRsvp = new Set(),
}: WeekDaySelectorProps) {
  return (
    <div className="flex gap-1 xs:gap-1.5 lg-mobile:gap-2">
      {weekDayDateKeys.map((dateKey) => {
        const dayEvents = eventsByDay.get(dateKey) || [];
        const hasEvents = dayEvents.length > 0;
        const hasMyRsvp = dateKeysWithMyRsvp.has(dateKey);
        const isSelected = selectionVisible && dateKey === selectedDateKey;
        const isTodayDate = dateKey === todayDateKey;
        const { dayOfMonth, weekdayIndex } =
          getDayOfMonthAndWeekdayBangkok(dateKey);

        const bgStyle = isSelected
          ? "bg-[var(--color-accent)] text-white shadow-md border border-transparent"
          : "bg-white/95 border border-slate-100 text-slate-600 active:bg-slate-50";

        return (
          <button
            key={dateKey}
            type="button"
            onClick={() => onSelectDateKey(dateKey)}
            className={`
              relative flex-1 flex flex-col items-center py-2 xs:py-2.5 lg-mobile:py-3 px-1 
              rounded-xl lg-mobile:rounded-2xl transition-all overflow-hidden
              ${bgStyle}
              ${isTodayDate && !isSelected ? "ring-2 ring-[var(--color-accent)] ring-offset-1" : ""}
            `}
          >
            <span className="relative text-[10px] xs:text-xs lg-mobile:text-sm font-medium opacity-70">
              {WEEKDAYS_SHORT[weekdayIndex]}
            </span>
            <span className="relative inline-flex items-center justify-center mt-0.5 min-w-[1.5em]">
              {hasMyRsvp && (
                <span
                  className="absolute inset-0 flex items-center justify-center translate-y-0.5 pointer-events-none"
                  aria-hidden
                >
                  <IconHeartFilled
                    className={`w-10 h-10 xs:w-12 xs:h-12 lg-mobile:w-14 lg-mobile:h-14 ${isSelected ? "text-white/30" : "text-orange-300/40"}`}
                  />
                </span>
              )}
              <span className="relative text-base xs:text-lg lg-mobile:text-xl font-semibold">
                {dayOfMonth}
              </span>
            </span>
            <span className="flex items-center justify-center mt-1 min-h-[6px] xs:min-h-[8px] shrink-0">
              {!hasMyRsvp && hasEvents && (
                <span
                  className={`
                    w-1.5 h-1.5 lg-mobile:w-2 lg-mobile:h-2 rounded-full shrink-0
                    ${isSelected ? "bg-white/80" : "bg-[var(--color-accent)]"}
                  `}
                />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

interface AgendaViewProps {
  selectedDateKey: BangkokDateKey;
  events: Event[];
  todayDateKey: BangkokDateKey;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

/** Agenda view showing events for selected day (Bangkok). */
function AgendaView({
  selectedDateKey,
  events,
  todayDateKey,
  isCollapsed,
  onToggleCollapsed,
}: AgendaViewProps) {
  const { weekdayIndex } = getDayOfMonthAndWeekdayBangkok(selectedDateKey);
  const dayName = WEEKDAYS_FULL[weekdayIndex];
  const dateStr = formatDateShortBangkok(selectedDateKey);
  const isTodayDate = selectedDateKey === todayDateKey;

  if (events.length === 0) {
    return (
      <div className={`${CARD_CLASS} p-6 lg-mobile:p-8 text-center`}>
        <IconCalendar className="w-10 h-10 lg-mobile:w-12 lg-mobile:h-12 mx-auto mb-3 lg-mobile:mb-4 text-slate-200" />
        <p className="text-slate-500 text-sm lg-mobile:text-base">
          {isTodayDate ? "Сегодня нет мероприятий" : "Нет мероприятий"}
        </p>
        <p className="text-slate-400 text-xs lg-mobile:text-sm mt-1">
          {dayName}, {dateStr}
        </p>
      </div>
    );
  }

  return (
    <div className={`${CARD_CLASS} p-4`}>
      <button
        type="button"
        onClick={() => {
          haptic("light");
          onToggleCollapsed();
        }}
        className="w-full flex items-center justify-between mb-3 lg-mobile:mb-4 text-left rounded-lg active:bg-slate-50 transition-colors"
      >
        <span className="text-sm font-medium text-slate-700">
          {isTodayDate ? "Сегодня" : dayName}
          <span className="font-normal text-slate-400 ml-2">{dateStr}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="text-xs lg-mobile:text-sm text-slate-500">
            {events.length}{" "}
            {events.length === 1
              ? "мероприятие"
              : events.length < 5
                ? "мероприятия"
                : "мероприятий"}
          </span>
          <IconChevronDown
            className={`w-4 h-4 lg-mobile:w-5 lg-mobile:h-5 text-slate-400 transition-transform shrink-0 ${isCollapsed ? "-rotate-90" : ""}`}
          />
        </span>
      </button>

      {!isCollapsed && (
        <div className="space-y-3 lg-mobile:space-y-4">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}

interface WeekAgendaViewProps {
  weekDayDateKeys: BangkokDateKey[];
  eventsByDay: Map<string, Event[]>;
  todayDateKey: BangkokDateKey;
  collapsedDayKeys: Set<string>;
  onToggleDayCollapsed: (dateKey: string) => void;
}

/** Agenda view for whole week: one section per day (Bangkok). */
function WeekAgendaView({
  weekDayDateKeys,
  eventsByDay,
  todayDateKey,
  collapsedDayKeys,
  onToggleDayCollapsed,
}: WeekAgendaViewProps) {
  const hasAnyEvents = weekDayDateKeys.some(
    (key) => (eventsByDay.get(key)?.length ?? 0) > 0,
  );

  if (!hasAnyEvents) {
    return (
      <div className={`${CARD_CLASS} p-6 lg-mobile:p-8 text-center`}>
        <IconCalendar className="w-10 h-10 lg-mobile:w-12 lg-mobile:h-12 mx-auto mb-3 lg-mobile:mb-4 text-slate-200" />
        <p className="text-slate-500 text-sm lg-mobile:text-base">
          На этой неделе нет мероприятий
        </p>
      </div>
    );
  }

  return (
    <div className={`${CARD_CLASS} p-4`}>
      <div className="space-y-6 lg-mobile:space-y-8">
        {weekDayDateKeys.map((dateKey) => {
          const dayEvents = eventsByDay.get(dateKey) || [];
          const { weekdayIndex } = getDayOfMonthAndWeekdayBangkok(dateKey);
          const dayName = WEEKDAYS_FULL[weekdayIndex];
          const dateStr = formatDateShortBangkok(dateKey);
          const isTodayDate = dateKey === todayDateKey;
          const isCollapsed = collapsedDayKeys.has(dateKey);

          return (
            <section key={dateKey}>
              {dayEvents.length > 0 ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      haptic("light");
                      onToggleDayCollapsed(dateKey);
                    }}
                    className="w-full flex items-center justify-between mb-3 lg-mobile:mb-4 text-left rounded-lg active:bg-slate-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-slate-700">
                      {isTodayDate ? "Сегодня" : dayName}
                      <span className="font-normal text-slate-400 ml-2">{dateStr}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs lg-mobile:text-sm text-slate-500">
                        {dayEvents.length}{" "}
                        {dayEvents.length === 1
                          ? "мероприятие"
                          : dayEvents.length < 5
                            ? "мероприятия"
                            : "мероприятий"}
                      </span>
                      <IconChevronDown
                        className={`w-4 h-4 lg-mobile:w-5 lg-mobile:h-5 text-slate-400 transition-transform shrink-0 ${isCollapsed ? "-rotate-90" : ""}`}
                      />
                    </span>
                  </button>
                  {!isCollapsed && (
                    <div className="space-y-3 lg-mobile:space-y-4">
                      {dayEvents.map((event) => (
                        <EventCard key={event.id} event={event} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3 lg-mobile:mb-4">
                    <span className="text-sm font-medium text-slate-700">
                      {isTodayDate ? "Сегодня" : dayName}
                      <span className="font-normal text-slate-400 ml-2">{dateStr}</span>
                    </span>
                  </div>
                  <p className="text-slate-400 text-xs lg-mobile:text-sm">
                    Нет мероприятий
                  </p>
                </>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SchedulePage() {
  const navigate = useNavigate();
  const tg = getTelegram();

  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const savedCategoryScrollLeftRef = useRef(0);
  const [collapsedDayKeys, setCollapsedDayKeys] = useState<Set<string>>(
    () => new Set(),
  );

  // Week navigation and filters (Bangkok timezone); restored from sessionStorage on mount
  const [scheduleState, setScheduleState] = useState<SchedulePageState>(
    getInitialScheduleState,
  );
  const {
    weekStartDateKey,
    selectedDateKey,
    selectedCategoryIds,
    showWholeWeek,
  } = scheduleState;

  // URQL hooks
  const { categories } = useScheduleCategories();
  
  const toDateKey = addDaysToDateKey(weekStartDateKey as BangkokDateKey, 6);
  const {
    events: rawEvents,
    isLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useEvents({
    categoryIds: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
    timezone: APP_TIMEZONE,
    fromDate: weekStartDateKey,
    toDate: toDateKey,
  });

  // Filter events client-side
  const events = useMemo(
    () => rawEvents.filter((e) => e.status !== "DRAFT" && e.isActive !== false),
    [rawEvents]
  );

  const error = eventsError?.message ?? null;

  const setWeekStartDateKey = useCallback((v: BangkokDateKey | ((prev: BangkokDateKey) => BangkokDateKey)) => {
    setScheduleState((prev) => ({
      ...prev,
      weekStartDateKey: typeof v === "function" ? v(prev.weekStartDateKey as BangkokDateKey) : v,
    }));
  }, []);
  const setSelectedDateKey = useCallback((v: BangkokDateKey | ((prev: BangkokDateKey) => BangkokDateKey)) => {
    setScheduleState((prev) => ({
      ...prev,
      selectedDateKey: typeof v === "function" ? v(prev.selectedDateKey as BangkokDateKey) : v,
    }));
  }, []);
  const setSelectedCategoryIds = useCallback(
    (v: string[] | ((prev: string[]) => string[])) => {
      setScheduleState((prev) => ({
        ...prev,
        selectedCategoryIds: typeof v === "function" ? v(prev.selectedCategoryIds) : v,
      }));
    },
    [],
  );
  const setShowWholeWeek = useCallback((v: boolean | ((prev: boolean) => boolean)) => {
    setScheduleState((prev) => ({
      ...prev,
      showWholeWeek: typeof v === "function" ? v(prev.showWholeWeek) : v,
    }));
  }, []);

  const todayDateKey = getTodayDateKeyBangkok();

  // Persist schedule state to sessionStorage when week/date/filters change
  useEffect(() => {
    const payload: SchedulePageState = {
      weekStartDateKey,
      selectedDateKey,
      selectedCategoryIds,
      showWholeWeek,
    };
    try {
      sessionStorage.setItem(SCHEDULE_STATE_KEY, JSON.stringify(payload));
    } catch {
      // ignore
    }
  }, [weekStartDateKey, selectedDateKey, selectedCategoryIds, showWholeWeek]);

  const weekDayDateKeys = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        addDaysToDateKey(weekStartDateKey as BangkokDateKey, i),
      ),
    [weekStartDateKey],
  );

  // Events grouped by day in Bangkok
  const eventsByDay = useMemo(() => {
    const map = new Map<string, Event[]>();
    for (const event of events) {
      const dateKey = getDateKeyBangkok(event.startsAt);
      const existing = map.get(dateKey);
      if (existing) {
        existing.push(event);
      } else {
        map.set(dateKey, [event]);
      }
    }
    for (const dayEvents of map.values()) {
      dayEvents.sort(
        (a, b) =>
          new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime(),
      );
    }
    return map;
  }, [events]);

  const selectedDayEvents = useMemo(
    () => eventsByDay.get(selectedDateKey) || [],
    [selectedDateKey, eventsByDay],
  );

  // Days where user has at least one RSVP or tickets
  const dateKeysWithMyRsvp = useMemo(() => {
    const set = new Set<string>();
    for (const event of events) {
      if (event.myRsvp || event.hasMyTickets) {
        set.add(getDateKeyBangkok(event.startsAt));
      }
    }
    return set;
  }, [events]);

  // Week label (Bangkok dates)
  const weekLabel = useMemo(() => {
    const startKey = weekStartDateKey;
    const endKey = addDaysToDateKey(weekStartDateKey, 6);
    const [, startM] = startKey.split("-").map(Number);
    const [, endM] = endKey.split("-").map(Number);
    const startDay = startKey.split("-")[2];
    const endDay = endKey.split("-")[2];
    if (startM === endM) {
      return `${startDay}–${endDay} ${MONTHS_GENITIVE[startM - 1]}`;
    }
    return `${startDay} ${MONTHS_GENITIVE[startM - 1]} – ${endDay} ${MONTHS_GENITIVE[endM - 1]}`;
  }, [weekStartDateKey]);

  const isCurrentWeek = weekStartDateKey === getWeekStartDateKeyBangkok();

  // Setup BackButton
  useEffect(() => {
    if (!tg) return;

    tg.BackButton.show();
    const handleBack = () => navigate(-1);
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  // Refetch on restore from background
  useOnRestoreFromBackground(() => {
    refetchEvents();
  });

  // Restore category filter scroll position after events load
  useEffect(() => {
    if (!isLoading && events.length >= 0 && categoryScrollRef.current) {
      const el = categoryScrollRef.current;
      const saved = savedCategoryScrollLeftRef.current;
      const restore = () => {
        el.scrollLeft = saved;
      };
      requestAnimationFrame(restore);
    }
  }, [isLoading, events.length]);

  const toggleCategory = useCallback((id: string) => {
    haptic("light");
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  }, []);

  const clearCategoryFilter = useCallback(() => {
    haptic("light");
    setSelectedCategoryIds([]);
  }, []);

  // Navigation handlers
  const goToPrevWeek = useCallback(() => {
    setWeekStartDateKey((prev) => addDaysToDateKey(prev, -7));
    setSelectedDateKey((prev) => addDaysToDateKey(prev, -7));
  }, []);

  const goToNextWeek = useCallback(() => {
    setWeekStartDateKey((prev) => addDaysToDateKey(prev, 7));
    setSelectedDateKey((prev) => addDaysToDateKey(prev, 7));
  }, []);

  const goToToday = useCallback(() => {
    setWeekStartDateKey(getWeekStartDateKeyBangkok());
    setSelectedDateKey(getTodayDateKeyBangkok());
  }, []);

  const handleSelectDateKey = useCallback((dateKey: BangkokDateKey) => {
    setSelectedDateKey(dateKey);
    setShowWholeWeek(false);
  }, []);

  const toggleDayCollapsed = useCallback((dateKey: string) => {
    setCollapsedDayKeys((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return next;
    });
  }, []);

  if (error) {
    return (
      <Layout>
        <ErrorDisplay
          error={error}
          onRetry={() => refetchEvents()}
          className={CARD_CLASS}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Category filters — QuickActions style */}
      <div
        ref={categoryScrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-hide pt-1.5 pb-1 mb-4 lg-mobile:mb-6 -mx-4 px-4 xs:-mx-5 xs:px-5"
        onScroll={(e) => {
          savedCategoryScrollLeftRef.current = (
            e.target as HTMLDivElement
          ).scrollLeft;
        }}
      >
        {/* Кнопка "Все" */}
        <button
          type="button"
          onClick={clearCategoryFilter}
          className={`flex-shrink-0 flex flex-col items-center gap-1.5 p-2.5 rounded-xl bg-white/95 shadow-sm border border-slate-100 transition-all active:scale-[0.97] min-w-[72px] ${
            selectedCategoryIds.length === 0
              ? "ring-2 ring-[var(--color-accent)] ring-offset-1"
              : ""
          }`}
        >
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-slate-100/80 text-slate-600/75">
            <IconLayers className="w-5 h-5" />
          </div>
          <span className="text-xs font-medium text-slate-700 whitespace-nowrap">
            Все
          </span>
        </button>

        {/* Кнопки категорий */}
        {categories.map((cat) => {
          const isSelected = selectedCategoryIds.includes(cat.id);
          const Icon = CATEGORY_ICONS[cat.code] ?? IconLayers;
          const colorClasses =
            CATEGORY_COLORS[cat.code] ?? "bg-slate-50/80 text-slate-700/75";

          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => toggleCategory(cat.id)}
              className={`flex-shrink-0 flex flex-col items-center gap-1.5 p-2.5 rounded-xl bg-white/95 shadow-sm border border-slate-100 transition-all active:scale-[0.97] min-w-[72px] ${
                isSelected ? "ring-2 ring-[var(--color-accent)] ring-offset-1" : ""
              }`}
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center ${colorClasses}`}
              >
                <Icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-medium text-slate-700 whitespace-nowrap">
                {cat.name}
              </span>
            </button>
          );
        })}
      </div>

      {/* Week navigation */}
      <div className={`${CARD_CLASS} mb-4 lg-mobile:mb-6 p-3 xs:p-4 lg-mobile:p-5`}>
        {/* Week header */}
        <div className="flex items-center justify-between mb-3 lg-mobile:mb-4">
          <button
            type="button"
            onClick={goToPrevWeek}
            className="flex items-center justify-center w-9 h-9 lg-mobile:w-11 lg-mobile:h-11 rounded-xl bg-slate-50/80 border border-slate-100 text-slate-500 active:bg-slate-100 transition-colors"
            aria-label="Предыдущая неделя"
          >
            <IconChevronLeft className="w-5 h-5 lg-mobile:w-6 lg-mobile:h-6" />
          </button>

          <div className="text-center min-w-0 flex-1">
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setShowWholeWeek((prev) => !prev);
              }}
              className={`
                inline-block text-center py-1.5 px-3 rounded-xl lg-mobile:rounded-2xl transition-colors
                text-sm xs:text-base lg-mobile:text-lg font-medium
                ${showWholeWeek ? "bg-[var(--color-accent)] text-white shadow-md" : "bg-slate-50/80 border border-slate-100 text-slate-800 active:bg-slate-100"}
              `}
              aria-label="Вся неделя"
            >
              {weekLabel}
            </button>
            {!isCurrentWeek && (
              <button
                type="button"
                onClick={goToToday}
                className="flex items-center justify-center gap-1 mx-auto mt-0.5 text-xs lg-mobile:text-sm text-[var(--color-accent)] active:opacity-70"
                aria-label="Перейти к сегодня"
              >
                <IconChevronDown className="w-3.5 h-3.5 lg-mobile:w-4 lg-mobile:h-4" />
                Сегодня
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={goToNextWeek}
            className="flex items-center justify-center w-9 h-9 lg-mobile:w-11 lg-mobile:h-11 rounded-xl bg-slate-50/80 border border-slate-100 text-slate-500 active:bg-slate-100 transition-colors"
            aria-label="Следующая неделя"
          >
            <IconChevronRight className="w-5 h-5 lg-mobile:w-6 lg-mobile:h-6" />
          </button>
        </div>

        {/* Week days (Bangkok) */}
        <WeekDaySelector
          weekDayDateKeys={weekDayDateKeys}
          selectedDateKey={selectedDateKey}
          onSelectDateKey={handleSelectDateKey}
          eventsByDay={eventsByDay}
          todayDateKey={todayDateKey}
          selectionVisible={!showWholeWeek}
          dateKeysWithMyRsvp={dateKeysWithMyRsvp}
        />
      </div>

      {/* Блок мероприятий: лоадер только здесь, остальная страница не трогаем */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[200px] py-12 bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-100 ring-1 ring-slate-200/50">
          <Loader className="mb-3" />
          <p className="text-xs text-slate-500">
            {events.length === 0 ? "Загрузка расписания..." : "Обновление..."}
          </p>
        </div>
      ) : showWholeWeek ? (
        <WeekAgendaView
          weekDayDateKeys={weekDayDateKeys}
          eventsByDay={eventsByDay}
          todayDateKey={todayDateKey}
          collapsedDayKeys={collapsedDayKeys}
          onToggleDayCollapsed={toggleDayCollapsed}
        />
      ) : (
        <AgendaView
          selectedDateKey={selectedDateKey}
          events={selectedDayEvents}
          todayDateKey={todayDateKey}
          isCollapsed={collapsedDayKeys.has(selectedDateKey)}
          onToggleCollapsed={() => toggleDayCollapsed(selectedDateKey)}
        />
      )}
    </Layout>
  );
}
